import type { ApplicationJourneyHeaderDto } from '../api/admin';
import type { IdentityLookupRow } from '../api/identityVerificationDashboardApi';
import type { LegacyLinkagePiiSnapshot } from './legacyLinkageDisplay';
import { formatIdentityDriftHeadline } from './legacyLinkageCopy';

function pickString(...vals: unknown[]): string | null {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

function splitFullName(fullName: string | null): { firstName: string | null; lastName: string | null } {
  if (!fullName) return { firstName: null, lastName: null };
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: null, lastName: null };
  if (parts.length === 1) return { firstName: parts[0] ?? null, lastName: null };
  return {
    firstName: parts[0] ?? null,
    lastName: parts.slice(1).join(' ') || null,
  };
}

/** Mirrors svc-legacy-linkage primary-given-name rule (first token only). */
export function primaryGivenName(raw: string | null | undefined): string {
  const normalized = normalizeName(raw);
  if (!normalized) return '';
  return normalized.split(' ')[0] ?? '';
}

/** Mirrors svc-legacy-linkage name normalization. */
export function normalizeName(raw: string | null | undefined): string {
  if (!raw) return '';
  return String(raw)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeDob(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Applicant PII from Identity tab / decision engine (live read). */
export function resolveLinkageApplicantPiiFromIdentity(
  application: ApplicationJourneyHeaderDto | null | undefined,
  identityRow: IdentityLookupRow | null | undefined,
): LegacyLinkagePiiSnapshot | null {
  const customer = identityRow?.customer ?? null;
  const ocr = identityRow?.ocr_data ?? null;
  const de = application?.decisionEngine;

  const firstName = pickString(
    customer?.first_name,
    ocr && typeof ocr === 'object' ? (ocr as Record<string, unknown>).first_name : null,
    ocr && typeof ocr === 'object' ? (ocr as Record<string, unknown>).firstName : null,
    de?.applicantFirstName,
  );
  const lastName = pickString(
    customer?.last_name,
    ocr && typeof ocr === 'object' ? (ocr as Record<string, unknown>).last_name : null,
    ocr && typeof ocr === 'object' ? (ocr as Record<string, unknown>).lastName : null,
    de?.applicantLastName,
  );
  const dateOfBirth = pickString(
    customer?.date_of_birth,
    ocr && typeof ocr === 'object' ? (ocr as Record<string, unknown>).date_of_birth : null,
    ocr && typeof ocr === 'object' ? (ocr as Record<string, unknown>).dateOfBirth : null,
  );

  if (firstName || lastName || dateOfBirth) {
    return { firstName, lastName, dateOfBirth };
  }

  const fromDeName = splitFullName(
    pickString([de?.applicantFirstName, de?.applicantLastName].filter(Boolean).join(' ')),
  );
  if (fromDeName.firstName || fromDeName.lastName) {
    return { ...fromDeName, dateOfBirth };
  }

  return null;
}

/** @deprecated use resolveLinkageApplicantPiiFromIdentity */
export const resolveLinkageApplicantPii = resolveLinkageApplicantPiiFromIdentity;

/** Prefer review-time snapshot; fall back to live identity OCR. */
export function resolveEffectiveApplicantPii(
  reviewSnapshot: LegacyLinkagePiiSnapshot | null | undefined,
  identitySnapshot: LegacyLinkagePiiSnapshot | null | undefined,
): LegacyLinkagePiiSnapshot | null {
  if (reviewSnapshot && (reviewSnapshot.firstName || reviewSnapshot.lastName || reviewSnapshot.dateOfBirth)) {
    return reviewSnapshot;
  }
  return identitySnapshot ?? null;
}

export function piiSnapshotsEqual(
  a: LegacyLinkagePiiSnapshot | null | undefined,
  b: LegacyLinkagePiiSnapshot | null | undefined,
): boolean {
  if (!a || !b) return false;
  return (
    normalizeName(a.firstName) === normalizeName(b.firstName) &&
    normalizeName(a.lastName) === normalizeName(b.lastName) &&
    normalizeDob(a.dateOfBirth) === normalizeDob(b.dateOfBirth)
  );
}

/** Live match flags from displayed rows (same rules as svc-legacy-linkage). */
export function computePiiMatchFlags(
  applicant: LegacyLinkagePiiSnapshot | null | undefined,
  legacy: LegacyLinkagePiiSnapshot | null | undefined,
): {
  firstNameMatch: boolean | null;
  lastNameMatch: boolean | null;
  dobMatch: boolean | null;
} {
  if (!applicant || !legacy) {
    return { firstNameMatch: null, lastNameMatch: null, dobMatch: null };
  }

  const probeGiven = primaryGivenName(applicant.firstName);
  const legacyGiven = primaryGivenName(legacy.firstName);
  const probeSurname = normalizeName(applicant.lastName);
  const legacySurname = normalizeName(legacy.lastName);
  const probeDob = normalizeDob(applicant.dateOfBirth);
  const legacyDob = normalizeDob(legacy.dateOfBirth);

  const firstNameMatch =
    probeGiven !== '' && legacyGiven !== '' ? probeGiven === legacyGiven : null;
  const lastNameMatch =
    probeSurname !== '' && legacySurname !== '' ? probeSurname === legacySurname : null;
  const dobMatch = probeDob !== null && legacyDob !== null ? probeDob === legacyDob : null;

  return { firstNameMatch, lastNameMatch, dobMatch };
}

export type LinkageComparisonField = {
  label: string;
  applicant: string;
  legacy: string;
  match: boolean | null;
  emphasizeMismatch?: boolean;
  hint?: string | null;
};

export function buildLinkageComparisonFields(
  applicant: LegacyLinkagePiiSnapshot | null | undefined,
  legacy: LegacyLinkagePiiSnapshot | null | undefined,
): LinkageComparisonField[] {
  const dash = '—';
  const flags = computePiiMatchFlags(applicant, legacy);
  const applicantTokens = normalizeName(applicant?.firstName).split(' ').filter(Boolean);
  const firstNameHint =
    applicantTokens.length > 1
      ? `Auto-link compares first word: ${titleCaseToken(applicantTokens[0] ?? '')}`
      : null;

  return [
    {
      label: 'First name',
      applicant: applicant?.firstName ?? dash,
      legacy: legacy?.firstName ?? dash,
      match: flags.firstNameMatch,
      emphasizeMismatch: flags.firstNameMatch === false,
      hint: firstNameHint,
    },
    {
      label: 'Last name',
      applicant: applicant?.lastName ?? dash,
      legacy: legacy?.lastName ?? dash,
      match: flags.lastNameMatch,
      emphasizeMismatch: flags.lastNameMatch === false,
    },
    {
      label: 'Date of birth',
      applicant: applicant?.dateOfBirth ?? dash,
      legacy: legacy?.dateOfBirth ?? dash,
      match: flags.dobMatch,
      emphasizeMismatch: flags.dobMatch === false,
    },
  ];
}

function titleCaseToken(token: string): string {
  if (!token) return token;
  return token.charAt(0) + token.slice(1).toLowerCase();
}

export function formatFaceSimilarity(distance?: number | null): string {
  if (typeof distance !== 'number' || !Number.isFinite(distance)) return 'Unknown';
  if (distance <= 0.05) return `Very strong (${distance.toFixed(3)})`;
  if (distance <= 0.25) return `Strong (${distance.toFixed(3)})`;
  if (distance <= 0.4) return `Moderate (${distance.toFixed(3)})`;
  return `Weak (${distance.toFixed(3)})`;
}

export function pendingReviewHeadline(piiReason?: string | null): string {
  switch (piiReason) {
    case 'given name differs':
      return 'Strong face match, but the first name does not match legacy.';
    case 'date of birth differs':
      return 'Face match found, but date of birth differs from legacy.';
    case 'given name missing on one side':
    case 'date of birth missing on one side':
      return 'Face match found, but identity details are incomplete on one side.';
    default:
      return 'Face match needs manual confirmation before funding.';
  }
}

/** Operator headline from live PII comparison (aligned with match icons). */
export function resolvePendingReviewHeadline(input: {
  applicant: LegacyLinkagePiiSnapshot | null | undefined;
  legacy: LegacyLinkagePiiSnapshot | null | undefined;
  identityDrift?: boolean;
  storedReason?: string | null;
  reviewApplicantPii?: LegacyLinkagePiiSnapshot | null;
  identityApplicantPii?: LegacyLinkagePiiSnapshot | null;
}): string {
  const {
    applicant,
    legacy,
    identityDrift,
    storedReason,
    reviewApplicantPii,
    identityApplicantPii,
  } = input;

  if (identityDrift && reviewApplicantPii && identityApplicantPii) {
    return formatIdentityDriftHeadline(
      formatPiiSnapshotLine(reviewApplicantPii),
      formatPiiSnapshotLine(identityApplicantPii),
    );
  }

  if (identityDrift) {
    return 'Identity verification differs from the linkage resolve snapshot — confirm the comparison rows before linking.';
  }

  if (!applicant || !legacy) {
    return pendingReviewHeadline(storedReason);
  }

  const flags = computePiiMatchFlags(applicant, legacy);

  if (flags.firstNameMatch === false) {
    return 'Strong face match, but the first name does not match legacy.';
  }
  if (flags.dobMatch === false) {
    return 'Face match found, but date of birth differs from legacy.';
  }
  if (flags.firstNameMatch === null || flags.dobMatch === null) {
    return 'Face match found, but identity details are incomplete on one side.';
  }
  if (flags.lastNameMatch === false) {
    return 'Strong face match with matching first name and date of birth, but last name differs — confirm before binding.';
  }

  return 'Strong face match with matching first name and date of birth — confirm this is the same person before binding.';
}

export function formatPiiSnapshotLine(pii: LegacyLinkagePiiSnapshot | null | undefined): string {
  if (!pii) return '—';
  const parts = [pii.firstName, pii.lastName, pii.dateOfBirth].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : '—';
}
